import { useMutation, useQuery } from '@tanstack/react-query';
import { DEFAULT_PAGE_SIZE } from '~/api/fetch';
import { routes } from '~/api/routes';
import { useChunkedGridDataQuery } from '~/data-grid/hooks/useChunkedGridDataQuery';
import { useFetch } from '~/hooks/useFetch';
import { dealListResponseSchema } from '~/schemas/deals';
import type {
  CreateDealRequest,
  RecogniseInboundTransactionWithSpotDealReconciliationRequest,
  UpdateDealTrancheRequest,
} from './schemas/deals.schemas';
import {
  createDealRequestSchema,
  createDealResponseSchema,
  dealOptionsResponseSchema,
  dealTrancheOptionsResponseSchema,
  recogniseInboundTransactionWithSpotDealReconciliationRequestSchema,
  updateDealTrancheRequestSchema,
  updateDealTrancheResponseSchema,
} from './schemas/deals.schemas';
import { asyncOperationResponseSchema } from './schemas/job-operations.schemas';

export const dealsQueryKeys = {
  getDealOptions: () => [...dealsQueryKeys.root, 'getDealOptions'],
  getDealTrancheOptions: (dealId: string, trancheId: string) => [
    ...dealsQueryKeys.root,
    'getDealTrancheOptions',
    dealId,
    trancheId,
  ],
  getDeals: () => [...dealsQueryKeys.root, 'getDeals'],
  root: ['deals'],
} as const;

export function useCreateDeal() {
  const { fetchData } = useFetch();

  return useMutation({
    mutationFn: (deal: CreateDealRequest) =>
      fetchData(
        routes.deals.createDeal,
        { request: createDealRequestSchema, response: createDealResponseSchema },
        {
          body: deal,
          method: 'POST',
        },
      ),
  });
}

export function useCreateSpotDeal(registryTransactionId: string) {
  const { fetchData } = useFetch();

  return useMutation({
    mutationFn: (spotDeal: RecogniseInboundTransactionWithSpotDealReconciliationRequest) =>
      fetchData(
        routes.registryTransactions.recognition.recogniseInboundTransactionWithSpotDealReconciliation(
          registryTransactionId,
        ),
        {
          request: recogniseInboundTransactionWithSpotDealReconciliationRequestSchema,
          response: asyncOperationResponseSchema,
        },
        {
          body: spotDeal,
          method: 'POST',
        },
      ),
  });
}

export function useUpdateDealTranche() {
  const { fetchData } = useFetch();

  return useMutation({
    mutationFn: ({
      dealId,
      tranche,
      trancheId,
    }: {
      dealId: string;
      tranche: UpdateDealTrancheRequest;
      trancheId: string;
    }) =>
      fetchData(
        routes.deals.updateDealTranche(dealId, trancheId),
        { request: updateDealTrancheRequestSchema, response: updateDealTrancheResponseSchema },
        {
          body: tranche,
          method: 'PATCH',
        },
      ),
  });
}

export function useGetDeals({ enabled = true }: { enabled?: boolean } = {}) {
  return useChunkedGridDataQuery({
    // Keep the list cached between page visits; creating a deal invalidates it
    clearCacheOnUnmount: false,
    dataField: 'deals',
    enabled,
    // Loads every deal for our largest org (9,844 in prod) in the first request
    firstChunkSize: DEFAULT_PAGE_SIZE,
    queryKeyPrefix: dealsQueryKeys.getDeals(),
    responseSchema: dealListResponseSchema,
    route: routes.deals.getDeals,
  });
}

export function useGetDealOptions({ enabled = true }: { enabled?: boolean } = {}) {
  const { fetchData } = useFetch();

  return useQuery({
    enabled,
    queryFn: () =>
      fetchData(
        routes.deals.getDealOptions,
        { response: dealOptionsResponseSchema },
        {
          method: 'OPTIONS',
        },
      ),
    queryKey: dealsQueryKeys.getDealOptions(),
  });
}

/** Which fields of the tranche an update can change, and the values they accept on the side of its deal. */
export function useGetDealTrancheOptions({ dealId, trancheId }: { dealId: string; trancheId: string }) {
  const { fetchData } = useFetch();

  return useQuery({
    // The editable fields follow the state of the tranche, so each opening of the edit form asks again.
    gcTime: 0,
    // The edit form shows the error in place of the fields.
    meta: { disableGlobalErrorHandler: true },
    queryFn: () =>
      fetchData(
        routes.deals.getDealTrancheOptions(dealId, trancheId),
        { response: dealTrancheOptionsResponseSchema },
        {
          method: 'OPTIONS',
        },
      ),
    queryKey: dealsQueryKeys.getDealTrancheOptions(dealId, trancheId),
  });
}
